import {
  BlotterTable,
  BlotterTableFilters,
  Box,
  Button,
  FormControlSizes,
  getShowJSONContextItem,
  HStack,
  IconName,
  useBlotterObservables,
  useBlotterTable,
  useCallbackRef,
  useGetDefaultContextMenuItems,
  useJsonModal,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams, RowSelectionOptions } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import type { SelectableCareOrder } from './SelectableCareOrder';
import { useCareOrderNettingPreviewColumns } from './useCareOrderNettingPreviewColumns';
import { useCareOrderNettingPreviewSelection } from './useCareOrderNettingPreviewSelection';

const memberCareOrdersRowSelection = {
  mode: 'multiRow',
  checkboxes: true,
  headerCheckbox: true,
  selectAll: 'filtered',
  enableClickSelection: true,
  enableSelectionWithoutKeys: true,
  copySelectedRows: true,
} as const satisfies RowSelectionOptions<SelectableCareOrder>;

interface MemberCareOrdersBlotterProps {
  isModificationMode?: boolean;
  portalId?: string;
  showAllDecimals: boolean;
  allCareOrders: SelectableCareOrder[];
  onSelectionChanged: (orderID: string, selected: boolean) => void;
  onShowCompatibleCareOrders: () => void;
  showCompatibleCareOrders: boolean;
}

export function MemberCareOrdersBlotter({
  isModificationMode = false,
  portalId,
  showAllDecimals,
  allCareOrders,
  onSelectionChanged,
  onShowCompatibleCareOrders,
  showCompatibleCareOrders,
}: MemberCareOrdersBlotterProps) {
  const displayedCareOrders = useMemo(() => {
    return allCareOrders.filter(careOrder => careOrder.isSelected);
  }, [allCareOrders]);

  const {
    selectedRows,
    updateCareOrderSelection: removeCareOrder,
    updateSelectedCareOrdersSelection: removeSelectedCareOrders,
    handleSelectionChanged,
  } = useCareOrderNettingPreviewSelection({
    action: 'remove',
    onSelectionChanged,
  });

  const columns = useCareOrderNettingPreviewColumns({
    actionColumn: {
      type: 'remove',
      onClick: removeCareOrder,
    },
  });
  const { dataObservable, errorObservable } = useBlotterObservables({
    data: displayedCareOrders,
  });
  const blotterSettings = useMemo(
    () => ({
      showAllDecimals,
    }),
    [showAllDecimals]
  );

  const { handleClickJson, jsonModal } = useJsonModal<SelectableCareOrder>();
  const getDefaultContextMenuItems = useGetDefaultContextMenuItems();

  const getContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams<SelectableCareOrder>) => {
    const showJsonItem = params?.node?.data ? getShowJSONContextItem({ params, handleClickJson }) : null;
    return compact([showJsonItem, showJsonItem ? 'separator' : null, ...getDefaultContextMenuItems(params)]);
  });

  const blotterTable = useBlotterTable<SelectableCareOrder>({
    dataObservable,
    errorObservable,
    rowID: 'OrderID' satisfies keyof SelectableCareOrder,
    columns,
    /* We add and remove rows from this blotter based on user actions. Scroll got messed up with 
    async transactions applying the state update. Going to synchronous updates fixed it. */
    applyTransactionsSynchronously: true,
    gridOptions: {
      rowSelection: memberCareOrdersRowSelection,
      getContextMenuItems,
      onSelectionChanged: handleSelectionChanged,
    },
    settings: blotterSettings,
  });

  return (
    <Box h="100%" data-testid="care-order-netting-preview-member-care-orders">
      <BlotterTableFilters
        portalId={portalId}
        {...blotterTable.blotterTableFiltersProps}
        showFilterBuilder={false}
        showDateRangePicker={false}
        suffix={
          <HStack gap="spacing6">
            <Button
              size={FormControlSizes.Small}
              onClick={removeSelectedCareOrders}
              startIcon={IconName.Close}
              disabled={selectedRows.length === 0}
              data-testid="care-order-netting-remove-selected-button"
            >
              Remove Selected
            </Button>
            {!showCompatibleCareOrders && (
              <Button
                size={FormControlSizes.Small}
                onClick={onShowCompatibleCareOrders}
                data-testid="care-order-netting-show-compatible-care-orders-button"
                startIcon={IconName.Plus}
              >
                More compatible orders
              </Button>
            )}
          </HStack>
        }
      />
      <Box h="100%">
        <BlotterTable {...blotterTable} />
      </Box>
      {jsonModal}
    </Box>
  );
}
